import { getFloorPlanHref, useNavigation } from '@/hooks/useNavigation'

import { HStack } from '@chakra-ui/react'

import { DrawerBreadcrumb } from '@/components/ui'
import { FacilityType } from '@/graphql/generated/schemas'
import { useAppDispatch } from '@/redux/hooks'
import { openDeviceDrawer, setDevice } from '@/redux/ui/uiSlice'
import { isDeviceDrawerSupported } from '@/utils/devices'
import { getArchivedLabel, getArchivedLabelWithSN } from '@/utils/formats'
import { getDefaultOrFirstFloorId } from '@/utils/helpers'

import { IncidentDataType } from '../shared/types/types'

export interface IncidentLocationIProps {
  incident: IncidentDataType
  isLoading: boolean
  onClose: () => void
}

export const IncidentLocationInfo = ({
  incident,
  onClose,
  isLoading,
}: IncidentLocationIProps) => {
  const device = incident?.devices?.[0]

  const isArchived = incident?.facility?.archived

  const facilityShortName = incident?.facility?.shortName

  const floorLabel = `Floor ${incident?.floor?.name}`
  const deviceName = device?.name

  const dispatch = useAppDispatch()
  const { goToFloorPlan } = useNavigation()

  // Preserve the ENG-22735 bug fix: navigation must wait for drawer closure
  // and React state updates, or the first click is missed.
  const goToFloor = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        goToFloorPlan(incident?.floor?.id)
      }, 0)
    })
  }

  const isNormalIncident = incident?.facility?.type === FacilityType.Normal

  // The Facility crumb lands on the incident's own floor when it has one, and
  // otherwise falls back to the facility's default (or first) floor — the same
  // floor clicking the facility in the Facilities tab opens — so the link
  // still resolves for a floorless incident (ENG-32089). Undefined when
  // neither exists, which correctly renders no link.
  const facilityFloorId =
    incident?.floor?.id ?? getDefaultOrFirstFloorId(incident?.facility)
  const facilityHref = isNormalIncident
    ? getFloorPlanHref(facilityFloorId)
    : undefined

  const goToFacility = () => {
    if (!facilityFloorId) return
    // Same deferral as goToFloor — navigation must wait for drawer closure.
    requestAnimationFrame(() => {
      setTimeout(() => {
        goToFloorPlan(facilityFloorId)
      }, 0)
    })
  }

  // Undefined for types DeviceDrawer can't render (Alarm, Checkpoint), which
  // renders the crumb as plain text rather than a dead link.
  const onDeviceDrawerOpen = isDeviceDrawerSupported(device?.type)
    ? () => {
        dispatch(setDevice({ id: device?.id, type: device?.type }))
        dispatch(openDeviceDrawer())
      }
    : undefined

  const fontSize = '14px'

  // Hacky, but makes it so that each section is as greedy as possible
  const madWidth = '100%'

  return (
    <HStack align='start' spacing={2}>
      {/* region Device */}
      <DrawerBreadcrumb
        dataTestId='incidentDrawer_header_deviceName'
        disabled={isArchived}
        fontSize={fontSize}
        hideDivider={false}
        isLoading={isLoading}
        isTruncated={true}
        label={deviceName}
        maxWidth={madWidth}
        onClick={onDeviceDrawerOpen}
        tooltipLabel={getArchivedLabel(deviceName, isArchived)}
      />
      {/* endregion */}

      {/* region Floor */}
      {(isLoading || incident?.floor?.name) && (
        <DrawerBreadcrumb
          dataTestId='incidentDrawer_header_floor'
          disabled={isArchived}
          fontSize={fontSize}
          hideDivider={false}
          href={getFloorPlanHref(incident?.floor?.id)}
          isLoading={isLoading}
          isTruncated={true}
          label={floorLabel}
          maxWidth={madWidth}
          onClick={goToFloor}
          onClose={onClose}
          tooltipLabel={getArchivedLabel(floorLabel, isArchived)}
        />
      )}
      {/* endregion */}

      {/* region Facility */}
      {/* b/c we're only using Facility short code we want this to be the "greediest" and not be truncated b/c it can't even be that big */}
      <DrawerBreadcrumb
        dataTestId='incidentDrawer_header_facilityShortName'
        disabled={isArchived}
        fontSize={fontSize}
        hideDivider={true}
        href={facilityHref}
        isLoading={isLoading}
        isTruncated={false}
        label={facilityShortName}
        maxWidth={madWidth}
        onClick={isNormalIncident && facilityFloorId ? goToFacility : undefined}
        onClose={isNormalIncident && facilityFloorId ? onClose : undefined}
        // The crumb shows only the short code, so the tooltip spells out the
        // full name and repeats the code it stands for (ENG-32089).
        tooltipLabel={getArchivedLabelWithSN(incident?.facility)}
      />
      {/* endregion */}
    </HStack>
  )
}
